import React, { FunctionComponent, useEffect } from 'react';

import { goToStepActionCreator } from 'src/state/order/action-creators/go-to-step-action-creator';
import { useProductsResponseQueryStateOrThrow } from 'src/hooks/use-products-query';
import { OrderStep } from 'src/state/order/types';
import { useAppDispatch } from 'src/state/thunk-api';
import { Spinner } from 'src/components/spinner';
import { i18n } from 'src/i18n';
import { withOrderStep } from 'src/hocs/with-order-step';
import { ProductName } from 'src/model/product-name';
import { isEligibleToUpgrade } from 'src/utils/equipment-utils';

import {
  StyledContainer,
  StyledNote,
  StyledProductSelectCard,
  StyledProductSelectCardContainer,
  StyledSubTitle,
  StyledTitle,
} from './product-select-page.styled';

export const ProductSelectPageComponent: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { upgradeDecoder } = useProductsResponseQueryStateOrThrow();
  const notEligibleToUpgrade = !isEligibleToUpgrade(upgradeDecoder);
  const products: ProductName[] = [ProductName.NEW_STB_ATV, ProductName.UPGRADE_STB_ATV];

  useEffect(() => {
    if (notEligibleToUpgrade) {
      dispatch(goToStepActionCreator(OrderStep.PURCHASE_DECODER, true));
    }
  }, [dispatch, notEligibleToUpgrade]);

  if (notEligibleToUpgrade) {
    return <Spinner />;
  }

  const handleSelectProduct = (productName: ProductName) => {
    switch (productName) {
      case 'NEW_STB_ATV':
        dispatch(goToStepActionCreator(OrderStep.PURCHASE_DECODER));
        break;
      case 'UPGRADE_STB_ATV':
        dispatch(goToStepActionCreator(OrderStep.UPGRADE_DECODER));
        break;
      default:
        throw new Error(`Invalid product name ${productName}`);
    }
  };

  return (
    <StyledContainer>
      <StyledTitle variant="headline2" component="h1">
        {i18n.page.productSelect.title}
      </StyledTitle>
      <StyledSubTitle variant="headline4" component="h2">
        {i18n.page.productSelect.subtitle}
      </StyledSubTitle>
      <StyledProductSelectCardContainer>
        {products.map((productName) => (
          <StyledProductSelectCard
            key={productName}
            buttonText={i18n.page.productSelect.productListing[productName].cta}
            onButtonClick={() => handleSelectProduct(productName)}
            title={i18n.page.productSelect.productListing[productName].title}
            descriptions={[
              i18n.page.productSelect.productListing[productName].mainDescription,
              i18n.page.productSelect.productListing[productName].description,
            ]}
          />
        ))}
      </StyledProductSelectCardContainer>
      <StyledNote variant="paragraph3" component="p">
        {i18n.page.productSelect.note}
      </StyledNote>
      <StyledNote variant="paragraph3" component="p">
        {i18n.page.productSelect.secondaryNote}
      </StyledNote>
    </StyledContainer>
  );
};

export const ProductSelectPage = withOrderStep(OrderStep.PRODUCT_SELECT)(ProductSelectPageComponent);
